/**
 * Translations Management Page
 *
 * View and manage translation jobs with filtering, linking, and bulk actions.
 * All tab content is rendered by sub-page classes for a unified UI.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

import { el, __, clear } from '../utils/dom.js';
import API from '../utils/api.js';
import { Button } from '../components/Button.js';
import Modal from '../components/Modal.js';
import Toast from '../components/Toast.js';
import { PageHeader } from '../components/PageHeader.js';
import { Tabs } from '../components/Tabs.js';

/**
 * Translations Page Class
 */
export default class TranslationsPage {
    /**
     * Marker so the router can identify this page without relying on constructor.name
     * (which may be mangled by minification).
     */
    isTranslationsPage = true;

    /**
     * Constructor
     *
     * @param {HTMLElement} container - Container element
     * @param {string|null}  initialTab - Tab to activate on first render (themes|pages|posts|plugins)
     */
    constructor(container, initialTab) {
        this.container = container;
        this.languages = [];

        // Tab state — order: Themes, Pages, Posts, Plugins
        const validTabs = ['themes', 'pages', 'posts', 'plugins'];
        this.activeTab = validTabs.includes(initialTab) ? initialTab : 'themes';
        this.subPage = null;
        this.tabContent = null;
        this.modal = null;
    }

    /**
     * Load available languages
     */
    async loadLanguages() {
        try {
            const data = await API.get('/languages');
            this.languages = data.data || data.languages || [];
        } catch (error) {
            Toast.error(error?.message || __('Failed to load languages', 'multilingual-press-zone'));
            this.languages = [];
        }
    }

    /**
     * Render the page
     */
    async render() {
        await this.loadLanguages();

        clear(this.container);

        const header = PageHeader({
            title: __('Translations', 'multilingual-press-zone'),
            description: __('Manage translation jobs and link content between languages', 'multilingual-press-zone'),
        });

        // Ensure the URL hash reflects the active tab on initial render
        history.replaceState(null, '', `#/translations/${this.activeTab}`);

        // Tabs — order: Themes, Pages, Posts, Plugins
        const tabs = Tabs({
            tabs: [
                { id: 'themes', label: __('Themes', 'multilingual-press-zone') },
                { id: 'pages', label: __('Pages', 'multilingual-press-zone') },
                { id: 'posts', label: __('Posts', 'multilingual-press-zone') },
                { id: 'plugins', label: __('Plugins', 'multilingual-press-zone') },
            ],
            activeTab: this.activeTab,
            onChange: (tabId) => this.switchTab(tabId)
        });

        // Tab content container
        this.tabContent = el('div', { id: 'mpz-translations-tab-content' });

        // Assemble page
        this.container.appendChild(header);
        this.container.appendChild(tabs);
        this.container.appendChild(this.tabContent);

        // Render the active tab
        await this.renderActiveTab();
    }

    /**
     * Switch to a different tab
     *
     * @param {string} tabId - Tab identifier (themes|pages|posts|plugins)
     * @param {Object} [options]
     * @param {boolean} [options.updateHash=true] - Whether to update the URL hash
     */
    async switchTab(tabId, { updateHash = true } = {}) {
        if (tabId === this.activeTab) return;

        // Cleanup current sub-page if exists
        if (this.subPage) {
            this.subPage.destroy();
            this.subPage = null;
        }

        this.activeTab = tabId;

        // Sync URL hash so the tab is bookmarkable / shareable
        if (updateHash) {
            history.replaceState(null, '', `#/translations/${tabId}`);
        }

        // Update tab button active states in the DOM.
        // The Tabs() component does not do this itself — only TabbedPanel does.
        const tablist = this.container.querySelector('[role="tablist"]');
        if (tablist) {
            tablist.querySelectorAll('[role="tab"]').forEach(btn => {
                const isActive = btn.dataset.tab === tabId;
                btn.classList.toggle('presszone-multilingual-tabs__item--active', isActive);
                btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
        }

        await this.renderActiveTab();
    }

    /**
     * Render content for the active tab
     */
    async renderActiveTab() {
        if (!this.tabContent) return;
        clear(this.tabContent);

        if (this.activeTab === 'pages' || this.activeTab === 'posts') {
            const postType = this.activeTab === 'pages' ? 'page' : 'post';
            const { default: ContentTranslatePage } = await import(
                /* webpackChunkName: "content-translate" */ './content-translate.js'
            );
            this.subPage = new ContentTranslatePage(
                this.tabContent,
                postType,
                this.languages
            );
            await this.subPage.render();
        } else {
            const sourceType = this.activeTab === 'themes' ? 'theme' : 'plugin';
            const { default: StringTranslatePage } = await import(
                /* webpackChunkName: "string-translate" */ './string-translate.js'
            );
            this.subPage = new StringTranslatePage(
                this.tabContent,
                sourceType,
                this.languages
            );
            await this.subPage.render();
        }
    }

    /**
     * Destroy the page
     */
    destroy() {
        if (this.modal) {
            this.modal.close();
            this.modal = null;
        }

        if (this.subPage) {
            this.subPage.destroy();
            this.subPage = null;
        }

        clear(this.container);
    }
}
